import { FC } from 'react';
import { useMutation } from '@apollo/client';

import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCompanyProfile } from 'common/hooks/useCompanyProfile';
import Button from 'components/_core/Button';
import useToast from 'components/ToastProvider/useToast';

import { refetchTeamMembersQuery } from '../../../../../../hooks/useTeamMembers';
import { refetchTeamMembersInvitationQuotaQuery } from '../../../../../../hooks/useTeamMembersInvitationQuota';
import TeamMemberModal from '../TeamMemberModal';
import { DeleteCompanyInvitationDocument } from './graphql/deleteCompanyInvitation.gql';
import locales from './locales';

interface InvitationDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    companyUserOrInvitationId: string;
    firstName: string;
    lastName: string;
  };
}

const InvitationDeletionModal: FC<InvitationDeletionModalProps> = ({
  isOpen,
  onClose,
  user: { companyUserOrInvitationId, firstName, lastName },
}) => {
  const { showToast } = useToast();
  const { companyProfileId = '' } = useCompanyContext();
  const { company } = useCompanyProfile(companyProfileId);

  const [deleteInvitationMutation, { loading }] = useMutation(
    DeleteCompanyInvitationDocument,
    {
      refetchQueries: [
        refetchTeamMembersQuery(companyProfileId),
        refetchTeamMembersInvitationQuotaQuery(companyProfileId),
      ],
      variables: {
        input: {
          companyInvitationId: companyUserOrInvitationId,
          companyProfileId,
        },
      },
    },
  );
  const onSubmit = async () => {
    await deleteInvitationMutation();
    showToast({
      message: locales.success,
      type: 'success',
    });
  };

  return (
    <TeamMemberModal
      description={locales.formatString(locales.description, {
        companyName: company?.legalName || '',
        firstName,
        lastName,
      })}
      footer={
        <>
          <Button
            isDisabled={loading}
            onClick={onClose}
            variant="inline-primary"
          >
            {locales.cancel}
          </Button>

          <Button isLoading={loading} onClick={onSubmit} variant="danger">
            {locales.ctaLabel}
          </Button>
        </>
      }
      isOpen={isOpen}
      title={locales.formatString(locales.title, {
        firstName,
        lastName,
      })}
    />
  );
};

export default InvitationDeletionModal;
